import React, { useEffect, useState } from "react";
import { PageLoading } from "../../../common/components/Loading";
import { NetworkRequest } from "../../../common/models";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { SurveyAnswers, useSurvey } from "../../../common/hooks/api/useSurvey";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import { DIRECT_MAIL_VENDORS_SLUG } from "../../surveys/sharingSurvey";
import { Api } from "../../../common/service/Api";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { parseMultiselectAnswerWithNoneOption } from "../../surveys/survey";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import { StepNumberCircle } from "./WorkflowSetupTabs";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UpdateVendorArgs } from "../data-map/components/workflows/create-data-recipient/ClassificationForDataRecipientPage";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import {
  ExplanatoryCard,
  Modal,
  SimpleCheckbox,
  Slideout,
  TextInput,
  Toggle,
  ToolTip,
} from "@truevault/aquilo";
import {
  BoltIcon,
  BoltSlashIcon,
  XMarkIcon,
  EnvelopeIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Form } from "react-final-form";
import { TRUEVAULT_BADGE_ICON } from "./DeleteInstructionsPage";
import { useSnackBar } from "@truevault/aquilo";
import clsx from "clsx";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { useHistory } from "react-router";
import { Routes } from "../../root/routes";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { alphabetically } from "../../surveys/util";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { VENDOR_CATEGORY_SALES_MARKETING_TOOL } from "../../types/Vendor";
import {
  getOptOutWorkflowVendorSelection,
  isVendorIncludedInOptOutWorkflow,
} from "../../util/optOutWorkflowVendors";

function filterOptOutVendors(
  allVendors: OrganizationDataRecipientDto[],
  sharingAnswers: SurveyAnswers,
  managedAdNetworks: OrganizationManagedAdNetworkDto[],
): {
  vendors: OrganizationDataRecipientDto[];
  customAudienceVendorManagers: string[];
  managedAdNetworksIds: string[];
  getManagementVendorForNetwork: (networkId: string) => OrganizationDataRecipientDto | null;
} {
  // Get the selected custom audience vendor managers from the survey
  const customAudienceVendorManagers = parseMultiselectAnswerWithNoneOption(
    sharingAnswers,
    "custom-audience-feature-separate-vendor",
  );

  // Get managed ad networks from database
  const getManagersForNetwork = (networkId: string) => {
    return (
      managedAdNetworks
        ?.filter(
          (item) =>
            item.networkId === networkId && customAudienceVendorManagers.includes(item.managerId),
        )
        ?.map((item) => allVendors?.find((v) => v.id === item.managerId))
        ?.filter(Boolean) || []
    );
  };

  // Get the first manager for a network (for backward compatibility with single-manager displays)
  const getManagementVendorForNetwork = (networkId: string) => {
    const managers = getManagersForNetwork(networkId);
    return managers[0] || null;
  };

  // Get all network IDs that are managed by someone (only if the manager is still selected)
  const managedAdNetworksIds: string[] = [
    ...new Set(
      managedAdNetworks
        ?.filter((item) => customAudienceVendorManagers.includes(item.managerId))
        ?.map((item) => item.networkId) || [],
    ),
  ];

  // Get all unique manager IDs
  const managementVendorIds: string[] = [
    ...new Set(managedAdNetworks?.map((item) => item.managerId) || []),
  ];

  // Filter management vendors to only include those that are still selected in the survey
  const managementVendors =
    allVendors?.filter(
      (v) => managementVendorIds.includes(v.id) && customAudienceVendorManagers.includes(v.id),
    ) || [];

  let vendors = allVendors?.filter((v) => {
    // If a management vendor exists, then hide vendors that are managed by the management vendor
    if (managementVendors.length > 0 && managedAdNetworksIds.includes(v.id)) {
      return false;
    }

    return (
      v.isUploadVendor ||
      v.isAlwaysUploadVendor ||
      v.usesCustomAudience ||
      v.ccpaIsSelling ||
      v.isAlwaysDataSellingVendor ||
      v.ccpaIsSharing
    );
  });

  // Add management vendors that should appear in opt-out instructions
  if (managementVendors.length > 0 && managedAdNetworksIds.length > 0) {
    const managementVendorsToAdd = managementVendors.filter((vendor) => {
      // Skip if already in vendors list
      if (vendors?.find((v) => v.id === vendor.id)) {
        return false;
      }

      // Include accessible vendors (current behavior)
      if (vendor.dataAccessibility === "ACCESSIBLE") {
        return true;
      }

      // Include inaccessible vendors that don't have an email (new requirement)
      if (vendor.dataAccessibility === "NOT_ACCESSIBLE" && !vendor.email && !vendor.defaultEmail) {
        return true;
      }

      return false;
    });

    if (managementVendorsToAdd.length > 0) {
      vendors?.push(...managementVendorsToAdd);
    }
  }
  vendors?.sort(alphabetically);

  return {
    vendors,
    managedAdNetworksIds,
    customAudienceVendorManagers,
    getManagementVendorForNetwork,
  };
}

export const OptOutInstructionsPage = () => {
  const history = useHistory();
  const snackbar = useSnackBar();
  // Constants

  // State
  const [readyToRender, setReadyToRender] = useState(false);
  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  // Requests
  const [organization, orgRequest, refreshOrg] = usePrimaryOrganization();
  const [allVendors, vendorsRequest, fetchVendors] = useOrganizationVendors(
    organization?.id,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ undefined,
    /* onlyUseSettled = */ true,
  );

  const [
    autoSendRequestEmailNotificationsForOptOutRequestsValue,
    setAutoSendRequestEmailNotificationsForOptOutRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForOptOutRequestsValue(
        organization.autoSendRequestEmailNotificationsForOptOutRequests,
      );
    }
  }, [organization]);

  const [instructions, setInstructions] = useState<null | RequestHandlingInstructionDto[]>(null);
  const [, instructionsRequest, fetchInstructions] = useRequestInstructions(
    organization.id,
    "OPT_OUT",
    (data: RequestHandlingInstructionDto[]) => {
      setInstructions(data);
    },
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(organization?.id, newInstruction);
    },
    onSuccess: fetchInstructions,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    // },
  });

  const { result: vendorMessageTemplatePreview } = useDataRequest({
    queryKey: ["vendorMessageTemplatePreview", organization?.id, "opt-out"],
    api: () =>
      Api.dataRequest.getRequestContactVendorsPreviewMessageTemplateForRequestType(
        organization?.id,
        "opt-out",
      ),
  });

  const {
    answers: sharingAnswers,
    setAnswer: setSharingAnswer,
    request: sharingSurveyRequest,
  } = useSurvey(organization.id, SELLING_AND_SHARING_SURVEY_NAME);

  // Determines whether to show the direct mail checkbox in the vendor sidebar
  // Initialized based on the presence or absence of any existing direct mail vendors
  const [enableDirectMailToggle, setEnableDirectMailToggle] = useState(null);
  useEffect(() => {
    if (sharingAnswers && enableDirectMailToggle === null) {
      const directMailVendorIds = parseMultiselectAnswerWithNoneOption(
        sharingAnswers,
        DIRECT_MAIL_VENDORS_SLUG,
      );
      setEnableDirectMailToggle(directMailVendorIds.length > 0);
    }
  }, [sharingAnswers]);
  const [showDirectMailDisableConfirmationModal, setShowDirectMailDisableConfirmationModal] =
    useState(false);

  const { settings: apiIntegrations } = useIntegrationsSettings();

  const { fetch: updateDataRecipient } = useActionRequest({
    api: async (v: UpdateVendorArgs) => {
      const { vendorId, vendorSettings } = v;
      await Api.organizationVendor.addOrUpdateVendor(organization?.id, vendorId, vendorSettings);
    },
    onSuccess: fetchVendors,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor data accessibility settings`, { variant: "error" });
    // },
  });

  const { managedAdNetworks } = useManagedAdNetworks(organization?.id);

  const {
    vendors,
    managedAdNetworksIds,
    customAudienceVendorManagers,
    getManagementVendorForNetwork,
  } = filterOptOutVendors(allVendors, sharingAnswers, managedAdNetworks);

  // Keep track of contact row column completion for enabling/disabling automation toggle
  const [dataRecipientContactEmailCompletionById, setDataRecipientContactEmailCompletionById] =
    useState<Map<string, boolean>>(null);

  // Initialize dataRecipientContactEmailCompletionById
  useEffect(() => {
    if (
      !allVendors ||
      allVendors.length == 0 ||
      !managedAdNetworks ||
      !sharingAnswers ||
      !instructions
    ) {
      return;
    }

    if (dataRecipientContactEmailCompletionById === null) {
      let { vendors: initVendors } = filterOptOutVendors(
        allVendors,
        sharingAnswers,
        managedAdNetworks,
      );
      let initMap = new Map<string, boolean>(
        initVendors.map((dr) => {
          let optOutInstruction = instructions?.find(
            (di) => di.vendorId == dr.vendorId && di.requestType == "OPT_OUT",
          );
          return [dr.id, optOutInstruction && isRowContactEmailCompleted(dr, optOutInstruction)];
        }),
      );
      setDataRecipientContactEmailCompletionById(initMap);
    }
  }, [
    allVendors,
    managedAdNetworks,
    sharingAnswers,
    instructions,
    dataRecipientContactEmailCompletionById,
  ]);

  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest, instructionsRequest, vendorsRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest, instructionsRequest, vendorsRequest]);

  if (
    !readyToRender ||
    !organization ||
    vendors === undefined ||
    !NetworkRequest.areFinished(sharingSurveyRequest) ||
    dataRecipientContactEmailCompletionById === null
  ) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  if (vendors !== undefined && vendors.length == 0) {
    return (
      <WorkflowSetupPageWrapper
        selectedTabKey="request-to-opt-out"
        nextOnClick={async () => {
          history.push(Routes.organization.requestHandling.accessRequest);
        }}
        nextDisabled={false}
      >
        <div className="tw-boundary">
          <div className="text-neutral-600 font-semibold mt-12 max-w-[776px]">
            Based on your data map you do not require an opt-out workflow. Your opt-outs are
            performed automatically on your website and logged in your closed requests.
          </div>
        </div>
      </WorkflowSetupPageWrapper>
    );
  }

  let missingContactEmailVendorNames = vendors
    .filter((v) => dataRecipientContactEmailCompletionById.get(v.id) !== true)
    .map((v) => v.name);
  let isAutomationToggleDisabled = missingContactEmailVendorNames.length > 0;
  let isAutomationToggleDisabledTooltipText = `Missing contact email for: ${missingContactEmailVendorNames.join(
    ", ",
  )}`;

  const optOutWorkflowSelection = getOptOutWorkflowVendorSelection(
    sharingAnswers,
    managedAdNetworks,
  );
  let directMailVendorIds = optOutWorkflowSelection.directMailVendorIds;

  let updateIncludedInDirectMail = async (
    dataRecipient: OrganizationDataRecipientDto,
    isIncluded: boolean,
  ) => {
    let newDirectMailVendorIds = new Set(directMailVendorIds);
    if (isIncluded) {
      newDirectMailVendorIds.add(dataRecipient.id);
    } else {
      newDirectMailVendorIds.delete(dataRecipient.id);
    }
    await setSharingAnswer(
      DIRECT_MAIL_VENDORS_SLUG,
      JSON.stringify(Array.from(newDirectMailVendorIds)),
    );
  };

  // Helper to check if a vendor is automated or not accessible
  const isVendorAutomatedOrNotAccessible = (vendor: OrganizationDataRecipientDto) => {
    // Vendor is "automated" if it has a connected API integration WITH opt-out workflow enabled
    const vendorApiConnection = (apiIntegrations?.vendors || []).find(
      (apiIntegration) => apiIntegration.vendorId === vendor.vendorId,
    );
    const isAutomated =
      vendorApiConnection?.connected === true && vendorApiConnection?.optOutEnabled === true;
    // Vendor is "not accessible" if it can't be accessed for automation
    const isNotAccessible = vendor.dataAccessibility === "NOT_ACCESSIBLE";
    return isAutomated || isNotAccessible;
  };

  const allCandidateVendorsForAutomation = [
    ...vendors,
    ...((allVendors || []).filter(
      (vendor) =>
        directMailVendorIds.includes(vendor.id) && !vendors.some((v) => v.id === vendor.id),
    ) || []),
  ];

  const isFullyAutomated = allCandidateVendorsForAutomation.every((vendor) => {
    if (!isVendorIncludedInOptOutWorkflow(vendor, optOutWorkflowSelection)) {
      return true;
    }
    return isVendorAutomatedOrNotAccessible(vendor);
  });

  const showFullyAutomatedCallout =
    isFullyAutomated && autoSendRequestEmailNotificationsForOptOutRequestsValue;
  const showEnableAutomationCallout =
    isFullyAutomated && !autoSendRequestEmailNotificationsForOptOutRequestsValue;

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-opt-out"
      nextOnClick={async () => {
        history.push(Routes.organization.requestHandling.accessRequest);
      }}
      nextDisabled={false}
      hideFooter={sidebarOpenForVendorId != null}
    >
      <>
        <div className="tw-boundary">
          <div className="max-w-[924px]">
            {showFullyAutomatedCallout && (
              <div className="flex items-center max-w-[924px] mb-6">
                <div>
                  <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl mr-3" />
                </div>
                <div className="text-green-700 font-medium text-sm">
                  Opt-Out request processing is fully automated. Closed requests will show up in
                  your "Closed" inbox tab.
                </div>
              </div>
            )}
            {showEnableAutomationCallout && (
              <div className="bg-blue-50 flex items-center max-w-[924px] mb-6 p-4 rounded-lg">
                <div>
                  <InformationCircleIcon className="w-4 h-4 text-blue-400 mr-3" />
                </div>
                <div className="text-blue-700 font-medium text-sm">
                  Toggle automation "on" in Step 2 below to fully automate the opt-out request
                  workflow.
                </div>
              </div>
            )}
            <ExplanatoryCard
              topBody={
                <div>
                  <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                    What is a Request to Opt-Out?
                  </h3>
                  <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                    <p>
                      A consumer's request that your business remove them from any data "selling"
                      and targeted advertising (also known as "sharing") involving third party
                      vendors. Typically this means removing the consumer's name and email address
                      from custom audience lists and other advertising outreach.
                    </p>
                    <p>
                      Your opt-out workflow will be automatically configured based on your inputs in
                      the table below.
                    </p>
                  </div>
                </div>
              }
              expandable={false}
            />

            <div className="flex items-center gap-3 mt-10">
              <StepNumberCircle text="1" />
              <div className="text-neutral-900 text-[18px] font-normal">
                Review vendors and their opt-out instructions
              </div>
            </div>

            <div className="mt-1.5 ml-[40px]">
              <OptOutInstructionsTable
                vendors={vendors}
                tableType="vendors"
                updateDataRecipient={updateDataRecipient}
                instructions={instructions}
                updateInstruction={updateInstruction}
                sidebarOpenForVendorId={sidebarOpenForVendorId}
                setSidebarOpenForVendorId={setSidebarOpenForVendorId}
                apiIntegrations={apiIntegrations}
                setDataRecipientContactEmailCompletionById={
                  setDataRecipientContactEmailCompletionById
                }
                managedAdNetworks={managedAdNetworks}
                managedAdNetworksIds={managedAdNetworksIds}
                getManagementVendorForNetwork={getManagementVendorForNetwork}
                customAudienceVendorManagers={customAudienceVendorManagers}
                allVendors={allVendors}
                directMailVendorIds={directMailVendorIds}
                updateIncludedInDirectMail={updateIncludedInDirectMail}
              />
            </div>

            <div className="mt-[24px] ml-[40px]">
              <div className="flex gap-[12px] items-center">
                <div className="w-[20px] h-[20px] rounded-[100px] bg-primary-100 flex items-center justify-center">
                  <EnvelopeIcon className="text-primary-500 w-[14px] h-[14px]" />
                </div>
                <div className="text-neutral-900 text-[14px] font-normal">
                  Optional: include direct mail vendors in opt-out processing
                </div>
                <ToolTip
                  text={
                    "Privacy opt-outs apply only to targeted online advertising, but many businesses choose to also remove consumers from promotional emails and mailers. Consumers may expect to be removed from such outreach when they exercise their opt-out right. Adjust vendor settings below. These settings can be changed at any time."
                  }
                  width="230px"
                  xOffset={-30}
                  yOffset={270}
                >
                  <InformationCircleIcon className="w-[16px] h-[16px] text-neutral-500" />
                </ToolTip>
                <Toggle
                  checked={enableDirectMailToggle}
                  onChange={async (newChecked: boolean) => {
                    if (newChecked === false && directMailVendorIds.length > 0) {
                      setShowDirectMailDisableConfirmationModal(true);
                    } else if (newChecked === false) {
                      await setSharingAnswer(DIRECT_MAIL_VENDORS_SLUG, "[]");
                      setEnableDirectMailToggle(false);
                    } else if (newChecked === true) {
                      setEnableDirectMailToggle(true);
                    }
                  }}
                />
              </div>
              {showDirectMailDisableConfirmationModal && (
                <DirectMailDisableConfirmationModal
                  closeModal={() => setShowDirectMailDisableConfirmationModal(false)}
                  onConfirm={async () => {
                    await setSharingAnswer(DIRECT_MAIL_VENDORS_SLUG, "[]");
                    setEnableDirectMailToggle(false);
                    setShowDirectMailDisableConfirmationModal(false);
                  }}
                />
              )}
            </div>

            {enableDirectMailToggle && (
              <div className="mt-[12px] ml-[40px]">
                <OptOutInstructionsTable
                  vendors={allVendors.filter((vendor) => {
                    // Only consider Sales & Marketing vendors
                    if (vendor.category !== VENDOR_CATEGORY_SALES_MARKETING_TOOL) {
                      return false;
                    }

                    // Don't show vendors that are already shown in the above vendors table
                    if (vendors.find((v) => v.id == vendor.id)) {
                      return false;
                    }

                    return true;
                  })}
                  tableType="direct-mail-vendors"
                  updateDataRecipient={updateDataRecipient}
                  instructions={instructions}
                  updateInstruction={updateInstruction}
                  sidebarOpenForVendorId={sidebarOpenForVendorId}
                  setSidebarOpenForVendorId={setSidebarOpenForVendorId}
                  apiIntegrations={apiIntegrations}
                  setDataRecipientContactEmailCompletionById={
                    setDataRecipientContactEmailCompletionById
                  }
                  managedAdNetworks={managedAdNetworks}
                  managedAdNetworksIds={managedAdNetworksIds}
                  getManagementVendorForNetwork={getManagementVendorForNetwork}
                  customAudienceVendorManagers={customAudienceVendorManagers}
                  allVendors={allVendors}
                  directMailVendorIds={directMailVendorIds}
                  updateIncludedInDirectMail={updateIncludedInDirectMail}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mt-10">
            <StepNumberCircle text="2" />
            <div className="text-gray-900 text-[18px] font-normal">
              Auto-send request processing emails
            </div>
            <ToolTip
              text={isAutomationToggleDisabled ? isAutomationToggleDisabledTooltipText : null}
            >
              <Toggle
                // Even if the automation setting is set to ON, if it becomes disabled (because of e.g. a new vendor was added with a missing contact email), then the automation won't work anyways, so show the automation toggle as OFF whenever it's disabled
                checked={
                  isAutomationToggleDisabled
                    ? false
                    : autoSendRequestEmailNotificationsForOptOutRequestsValue
                }
                disabled={isAutomationToggleDisabled}
                onChange={async (checked) => {
                  setAutoSendRequestEmailNotificationsForOptOutRequestsValue(checked);
                  await Api.organization.updateOrganization(organization.id, {
                    name: organization.name,
                    logoUrl: organization.logoUrl,
                    regulations: [],
                    autoSendRequestEmailNotificationsForOptOutRequests: checked,
                  });
                  await refreshOrg();

                  if (checked) {
                    snackbar("Enabled automatically sending opt-out request email notifications", {
                      variant: "success",
                    });
                  } else {
                    // closeSnackbar functionality not available in new API
                  }
                }}
              />
            </ToolTip>
          </div>
          <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
            If this is toggled off, you will be prompted to manually click a button to send the
            vendor email and consumer notification email below for each request you process.
          </div>
        </div>

        {/* Put the email templates outside of tw-boundary since the template HTML will only render correctly with default styles, and will not render correctly if overriden with tailwind styles */}
        <div style={{ marginTop: "12px", marginLeft: "40px" }}>
          <div className="tw-boundary">
            <div className="text-neutral-900 text-[14px]">Vendor Email Notification Template</div>
          </div>

          <div
            style={{
              backgroundColor: "#F9F8FB",
              padding: "32px",
              maxWidth: "736px",
              marginTop: "6px",
            }}
          >
            <p>
              <strong>To:</strong> <span>[recipient email]</span>
            </p>

            <p>
              <strong>Subject: Request to Opt-Out</strong>
            </p>

            <div dangerouslySetInnerHTML={{ __html: vendorMessageTemplatePreview }}></div>
          </div>
        </div>
      </>
    </WorkflowSetupPageWrapper>
  );
};

function OptOutInstructionsTable({
  vendors,
  tableType,
  updateDataRecipient,
  instructions,
  updateInstruction,
  updateIncludedInDirectMail,
  sidebarOpenForVendorId,
  setSidebarOpenForVendorId,
  allVendors,
  apiIntegrations,
  setDataRecipientContactEmailCompletionById,
  directMailVendorIds,
  managedAdNetworks,
  managedAdNetworksIds,
  getManagementVendorForNetwork,
  customAudienceVendorManagers,
}: {
  vendors: OrganizationDataRecipientDto[];
  tableType: "vendors" | "direct-mail-vendors";
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  instructions: RequestHandlingInstructionDto[];
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  updateIncludedInDirectMail: (
    dataRecipient: OrganizationDataRecipientDto,
    isIncluded: boolean,
  ) => Promise<void>;
  sidebarOpenForVendorId: string | null;
  setSidebarOpenForVendorId: (vendorId: string | null) => void;
  allVendors: OrganizationDataRecipientDto[];
  apiIntegrations: PrivacyCenterIntegrationsDto;
  setDataRecipientContactEmailCompletionById: React.Dispatch<
    React.SetStateAction<Map<string, boolean>>
  >;
  directMailVendorIds: string[];
  managedAdNetworks: any[];
  managedAdNetworksIds: string[];
  getManagementVendorForNetwork: (networkId: string) => OrganizationDataRecipientDto | null;
  customAudienceVendorManagers: string[];
}) {
  return (
    <div className="border border-solid border-gray-200 rounded-lg max-w-[924px] max-h-[636px] overflow-y-auto relative">
      <table>
        <colgroup>
          <col style={{ width: "338px" }} />
          <col style={{ width: "188px" }} />
          <col style={{ width: "338px" }} />
          <col style={{ width: "60px" }} />
        </colgroup>
      </table>
      <thead className="sticky top-0 z-9">
        <tr>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tl-lg pl-6 text-left text-xs font-medium text-gray-500 uppercase"
          >
            Vendor
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
          >
            {tableType === "vendors" ? "Contact for Requests" : "Add To Opt-Out Workflow"}
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 text-center text-xs font-medium text-gray-500 uppercase"
          >
            Opt Out Instructions
          </th>
          <th
            scope="col"
            className="bg-[#F9FAFB] h-[40px] border-b border-solid border-gray-200 rounded-tr-lg text-center text-xs font-medium text-gray-500 uppercase"
          >
            &nbsp;
          </th>
        </tr>
      </thead>
      <tbody>
        {vendors?.map((dataRecipient) => {
          let optOutInstruction = instructions?.find(
            (i) => i.vendorId == dataRecipient.vendorId && i.requestType == "OPT_OUT",
          );

          if (!optOutInstruction) {
            return null;
          }

          // Check if this vendor is managed by an audience management platform
          const isManaged = managedAdNetworksIds.includes(dataRecipient.id);
          const managingVendor = isManaged ? getManagementVendorForNetwork(dataRecipient.id) : null;

          return (
            <DataRecipientRowWithSidebar
              key={dataRecipient.id}
              dataRecipient={dataRecipient}
              tableType={tableType}
              optOutInstruction={optOutInstruction}
              updateDataRecipient={updateDataRecipient}
              updateInstruction={updateInstruction}
              openSidebar={() => setSidebarOpenForVendorId(dataRecipient.vendorId)}
              isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
              closeSidebar={() => setSidebarOpenForVendorId(null)}
              apiIntegrations={apiIntegrations}
              managingVendor={managingVendor}
              managedAdNetworks={managedAdNetworks}
              allVendors={allVendors}
              customAudienceVendorManagers={customAudienceVendorManagers}
              setContactEmailCompletionForDataRecipient={(
                drId: string,
                contactEmailCompleted: boolean,
              ) => {
                setDataRecipientContactEmailCompletionById((prev) => {
                  let newMap = new Map(prev);
                  newMap.set(drId, contactEmailCompleted);
                  return newMap;
                });
              }}
              isIncludedInDirectMail={directMailVendorIds.includes(dataRecipient.id)}
              enableDirectMailToggle={
                tableType === "direct-mail-vendors" ||
                directMailVendorIds.includes(dataRecipient.id)
              }
              updateIncludedInDirectMail={updateIncludedInDirectMail}
            />
          );
        })}
      </tbody>
    </div>
  );
}

function DataRecipientRowWithSidebar({
  dataRecipient,
  optOutInstruction,
  updateDataRecipient,
  updateInstruction,
  tableType,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  apiIntegrations,
  setContactEmailCompletionForDataRecipient,
  managingVendor,
  managedAdNetworks,
  allVendors,
  customAudienceVendorManagers,
  enableDirectMailToggle,
  isIncludedInDirectMail,
  updateIncludedInDirectMail,
}: {
  dataRecipient: OrganizationDataRecipientDto;
  optOutInstruction: RequestHandlingInstructionDto;
  updateDataRecipient: (dataRecipient: UpdateVendorArgs) => void;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  tableType: "vendors" | "direct-mail-vendors";
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  setContactEmailCompletionForDataRecipient: (id: string, contactEmailCompleted: boolean) => void;
  managingVendor: OrganizationDataRecipientDto | null;
  managedAdNetworks: any[];
  allVendors: OrganizationDataRecipientDto[];
  customAudienceVendorManagers: string[];
  enableDirectMailToggle: boolean;
  isIncludedInDirectMail: boolean;
  updateIncludedInDirectMail: (
    vendor: OrganizationDataRecipientDto,
    included: boolean,
  ) => Promise<void>;
}) {
  const [email, setEmail] = useState(dataRecipient.email || dataRecipient.defaultEmail);
  const [doNotContactValue, setDoNotContactValue] = useState(optOutInstruction.doNotContact);
  const [userProvidedOptOutInstructions, setUserProvidedOptOutInstructions] = useState(
    optOutInstruction.processingInstructions,
  );

  const dataRecipientWithLocalChanges = {
    ...dataRecipient,
  };
  // only set email if it is not empty
  if (email && email.length > 0) {
    dataRecipientWithLocalChanges.email = email;
  }

  const optOutInstructionWithLocalChanges: RequestHandlingInstructionDto = {
    ...optOutInstruction,
    doNotContact: doNotContactValue,
    processingInstructions: userProvidedOptOutInstructions,
  };

  let hasVendorOptOutInstructions = dataRecipientWithLocalChanges.optOutInstructions?.length > 0;

  // If this vendor is managed, use the managing vendor's instructions
  const effectiveVendor = managingVendor || dataRecipientWithLocalChanges;
  const effectiveOptOutInstructions = effectiveVendor?.optOutInstructions;
  hasVendorOptOutInstructions = effectiveOptOutInstructions?.length > 0;

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipientWithLocalChanges.id}
        className={clsx("border-t border-solid border-gray-200", "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td className="px-6 py-3.5 w-[338px] max-w-[338px] border-b border-solid border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <img
                src={dataRecipientWithLocalChanges.logoUrl}
                alt={dataRecipientWithLocalChanges.name}
                className="h-6 w-6"
              />
              <div className="flex flex-col">
                <span className="text-[#111827] font-medium text-sm">
                  {dataRecipientWithLocalChanges.name}
                </span>
                {managingVendor && (
                  <span className="text-gray-500 text-xs">(managed by {managingVendor.name})</span>
                )}
              </div>
              {vendorApiConnection &&
                (vendorApiConnection.connected ? (
                  <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
                ) : (
                  <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
                ))}
            </div>
            {(() => {
              // Show which networks this vendor manages (only if they are an active manager)
              const networksManaged =
                managedAdNetworks
                  ?.filter(
                    (item) =>
                      item.managerId === dataRecipientWithLocalChanges.id &&
                      customAudienceVendorManagers.includes(item.managerId),
                  )
                  ?.map((item) => allVendors?.find((v) => v.id === item.networkId))
                  ?.filter(Boolean) || [];

              if (networksManaged.length === 0) return null;

              return (
                <div className="mt-2">
                  <div className="text-gray-600 text-xs font-medium">
                    Managing: {networksManaged.map((network) => network?.name).join(", ")}
                  </div>
                </div>
              );
            })()}
          </div>
        </td>
        <td className="text-center w-[188px] max-w-[188px] border-b border-solid border-gray-200">
          {tableType === "vendors"
            ? contactForRequestsColumnValue(
                dataRecipientWithLocalChanges,
                optOutInstructionWithLocalChanges,
              )
            : directMailColumnValue(dataRecipientWithLocalChanges, isIncludedInDirectMail)}
        </td>
        <td className="text-center w-[338px] max-w-[338px] border-b border-solid border-gray-200 break-all">
          {optOutInstructionsColumnValue(
            dataRecipientWithLocalChanges,
            optOutInstructionWithLocalChanges,
            managingVendor,
            vendorApiConnection,
          )}
        </td>
        <td className="w-[60px] max-w-[60px] border-b border-solid border-gray-200">
          <ArrowRightCircleIcon
            className="w-[20px] h-[20px] text-primary-600 cursor-pointer"
            onClick={openSidebar}
          />
        </td>
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-gray-900 text-[18px] font-bold">
                Request to Opt-Out Vendor Settings
              </h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center">
                <img
                  src={dataRecipientWithLocalChanges.logoUrl}
                  alt={dataRecipientWithLocalChanges.name}
                  className="h-5 w-5"
                />
                <span className="text-gray-900 font-medium text-[18px] ml-[12px] mr-[12px]">
                  {dataRecipientWithLocalChanges.name}
                </span>
                {dataRecipientWithLocalChanges.dataAccessibility === "NOT_ACCESSIBLE" && (
                  <div className="bg-neutral-200 rounded-[6px] py-[4px] px-[8px] flex items-center">
                    <span className="text-gray-800 text-[12px] font-medium">Not Accessible</span>
                  </div>
                )}
                {vendorApiConnection && vendorApiConnection.connected && (
                  <AutomationConnectedBadge />
                )}
              </div>
              {dataRecipientWithLocalChanges.url?.length > 0 && (
                <div className="mt-1 text-[14px] font-normal text-gray-500">
                  {dataRecipientWithLocalChanges.url}
                </div>
              )}
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="p-6">
                      {dataRecipientWithLocalChanges.dataAccessibility === "NOT_ACCESSIBLE" &&
                        !email && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                              This vendor is not accessible for automated opt-outs. Please add a
                              contact email address so you can manually send opt-out requests when
                              needed.
                            </p>
                          </div>
                        )}
                      <div>
                        <label className="block text-[14px] font-medium text-gray-700">
                          Contact Email for Requests
                        </label>
                        <TextInput
                          field="vendor-opt-out-contact-email"
                          onBlur={async (e: React.FocusEvent<HTMLInputElement>) => {
                            let newEmail = e.target.value;
                            setEmail(newEmail);
                            let updatedDataRecipient = {
                              ...dataRecipientWithLocalChanges,
                              email: newEmail,
                            };
                            await updateDataRecipient({
                              vendorId: dataRecipientWithLocalChanges.vendorId,
                              vendorSettings: {
                                ...updatedDataRecipient,
                                classificationId: dataRecipientWithLocalChanges.classification.id,
                              },
                            });

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                updatedDataRecipient,
                                optOutInstructionWithLocalChanges,
                              ),
                            );
                          }}
                          defaultValue={email}
                          maxLength={90}
                          disabled={doNotContactValue}
                        />
                      </div>
                      <div className="mt-6">
                        <SimpleCheckbox
                          name="doNotContact"
                          label={`Do Not Contact ${dataRecipient.name} for Opt-Out Requests`}
                          initialChecked={optOutInstruction?.doNotContact}
                          onChange={async () => {
                            let newDoNotContact = !doNotContactValue;

                            setDoNotContactValue(newDoNotContact);

                            let updatedInstruction = {
                              ...optOutInstructionWithLocalChanges,
                              doNotContact: newDoNotContact,
                            };

                            await updateInstruction(updatedInstruction);

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                dataRecipientWithLocalChanges,
                                updatedInstruction,
                              ),
                            );
                          }}
                        />
                      </div>
                    </div>
                    <hr className="border-gray-200" />
                    {enableDirectMailToggle && (
                      <>
                        <div className="p-6">
                          <SimpleCheckbox
                            name="includeInDirectMail"
                            label={`Include vendor in direct mail opt-out process`}
                            initialChecked={isIncludedInDirectMail}
                            onChange={async (newChecked: boolean) => {
                              await updateIncludedInDirectMail(
                                dataRecipientWithLocalChanges,
                                newChecked,
                              );
                            }}
                          />
                        </div>
                        <hr className="border-gray-200" />
                      </>
                    )}
                    <div className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-[14px] text-gray-700 font-medium">
                          {managingVendor
                            ? `Self Service Opt-Out Instructions (via ${managingVendor.name})`
                            : "Self Service Opt-Out Instructions"}
                        </div>
                        {hasVendorOptOutInstructions && (
                          <div className="flex items-center gap-1">
                            {TRUEVAULT_BADGE_ICON}
                            <span className="text-primary-600 text-[10px] font-medium">
                              Verified
                            </span>
                          </div>
                        )}
                      </div>
                      {hasVendorOptOutInstructions && (
                        <div className="vendor-instructions mt-3">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: effectiveOptOutInstructions,
                            }}
                          />
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-neutral-700 text-[14px] font-medium">
                          {hasVendorOptOutInstructions
                            ? "Add Your Opt-Out Instructions (optional)"
                            : "Add Your Opt-Out Instructions"}
                        </div>
                        <textarea
                          rows={4}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                          defaultValue={userProvidedOptOutInstructions}
                          placeholder="E.g. find the consumer first at the settings.."
                          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                            let newUserProvidedOptOutInstructions = e.target.value;

                            setUserProvidedOptOutInstructions(newUserProvidedOptOutInstructions);

                            let updatedInstruction = {
                              ...optOutInstructionWithLocalChanges,
                              processingInstructions: newUserProvidedOptOutInstructions,
                            };

                            await updateInstruction(updatedInstruction);

                            setContactEmailCompletionForDataRecipient(
                              dataRecipientWithLocalChanges.id,
                              isRowContactEmailCompleted(
                                dataRecipientWithLocalChanges,
                                updatedInstruction,
                              ),
                            );
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </>
      </Slideout>
    </>
  );
}

function DirectMailDisableConfirmationModal({
  closeModal,
  onConfirm,
}: {
  closeModal: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Modal
      title="Are you sure you want to disable direct mail vendors in opt-out processing?"
      alignText="left"
      firstButton="Cancel"
      firstButtonColor="tertiary"
      onFirstButtonClick={closeModal}
      alignButton="right"
      secondButton="OK"
      secondButtonColor="primary"
      onSecondButtonClick={onConfirm}
      showOverlay
      closeOnOverlayClick={false}
      onClose={closeModal}
    >
      <p className="text-gray-500 text-[14px] font-normal mt-2 leading-[20px]">
        This will clear the list of vendors included in direct mail opt-out processing. If you want
        to re-enable direct mail vendors, you will need to add them again.
      </p>
    </Modal>
  );
}

const contactForRequestsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";
  let email = vendor.email || vendor.defaultEmail;

  if (optOutInstruction?.doNotContact) {
    resultLabel = "Do Not Contact";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else if (email) {
    resultLabel = email;
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "Email Address Needed";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 max-w-[188px] truncate font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

const directMailColumnValue = (
  vendor: OrganizationDataRecipientDto,
  isIncludedInDirectMail: boolean,
): React.ReactNode => {
  let resultLabel = "",
    textColor = "",
    bgColor = "";

  if (isIncludedInDirectMail) {
    resultLabel = "Yes";
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else {
    resultLabel = "No";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 max-w-[188px] truncate font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

const optOutInstructionsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
  managingVendor?: OrganizationDataRecipientDto | null,
  vendorApiConnection?: { connected: boolean; optOutEnabled: boolean } | null,
): React.ReactNode => {
  let resultLabel: React.ReactNode = "",
    textColor = "",
    bgColor = "";

  // Check if vendor has active opt-out automation (highest priority)
  const isAutomatedForOptOut =
    vendorApiConnection?.connected === true && vendorApiConnection?.optOutEnabled === true;

  // If this vendor is managed, show the managing vendor's instructions instead
  const effectiveVendor = managingVendor || vendor;
  const hasOptOutInstructions = effectiveVendor?.optOutInstructions?.length > 0;

  if (isAutomatedForOptOut) {
    resultLabel = "Automated";
    textColor = "text-green-700";
    bgColor = "bg-green-100";
  } else if (hasOptOutInstructions) {
    resultLabel = (
      <div className="flex items-center gap-1">
        {TRUEVAULT_BADGE_ICON}
        <span className="text-primary-600">
          {managingVendor ? `Verified (via ${managingVendor.name})` : "Verified"}
        </span>
      </div>
    );
    bgColor = "bg-[#EDE9FE]";
  } else if (optOutInstruction?.processingInstructions) {
    resultLabel = "Your Instructions";
    textColor = "text-[#1E40AF]";
    bgColor = "bg-[#DBEAFE]";
  } else if (vendor.dataAccessibility === "NOT_ACCESSIBLE") {
    resultLabel = "Not Accessible";
    textColor = "text-gray-800";
    bgColor = "bg-neutral-200";
  } else {
    resultLabel = "Add Instructions";
    textColor = "text-[#92400E]";
    bgColor = "bg-[#FEF3C7]";
  }

  return (
    <div
      className={`inline-block px-2.5 py-0.5 font-medium text-sm rounded-full ${textColor} ${bgColor}`}
    >
      {resultLabel}
    </div>
  );
};

const isRowContactEmailCompleted = (
  dataRecipient: OrganizationDataRecipientDto,
  optOutInstruction: RequestHandlingInstructionDto,
): boolean => {
  // First make sure we actually need to contact the vendor
  if (optOutInstruction?.doNotContact || dataRecipient.dataAccessibility === "NOT_ACCESSIBLE") {
    return true;
  }

  // Check if we have either an email or a default email
  return Boolean(dataRecipient.email || dataRecipient.defaultEmail);
};

export function AutomationConnectedBadge() {
  return (
    <div className="bg-green-100 rounded-[6px] py-[6px] px-[10px] flex items-center">
      <div>
        <BoltIcon className="w-[12px] h-[14px] text-green-600" />
      </div>
      <div className="text-green-600 text-[14px] font-medium ml-[8px]">Connected</div>
    </div>
  );
}
